import { describe, it, expect } from '@jest/globals';
import type { AppConfig } from '@ms-cloudpack/common-types';
import { mergeParentConfig } from './mergeParentConfig.js';

describe('mergeParentConfig', () => {
  it('removes extends from merged config', () => {
    const appConfig: AppConfig = {
      extends: 'parent-config',
      server: { port: 8080 },
    };

    const parentConfig: AppConfig = {
      helpMessage: 'help',
    };

    const result = mergeParentConfig({ appConfig, parentConfigs: [parentConfig] });

    expect(result).toEqual({
      server: { port: 8080 },
      helpMessage: 'help',
    });
  });

  it('prefers properties from user config', () => {
    const appConfig: AppConfig = {
      helpMessage: 'user help',
    };

    const parentConfig: AppConfig = {
      helpMessage: 'parent help',
    };

    const result = mergeParentConfig({ parentConfigs: [parentConfig], appConfig });

    expect(result).toEqual({
      helpMessage: 'user help',
    });
  });

  it('prefers properties from last parent config', () => {
    const appConfig: AppConfig = {};

    const parentConfig: AppConfig = {
      helpMessage: 'parent help',
    };

    const extraConfig: AppConfig = {
      helpMessage: 'extra help',
    };

    const result = mergeParentConfig({ parentConfigs: [parentConfig, extraConfig], appConfig });

    expect(result).toEqual({
      helpMessage: 'extra help',
    });
  });

  it('recursively merges', () => {
    const appConfig: AppConfig = {
      extends: 'parent-config',
      server: {
        port: 8080,
        domain: 'localhost',
        https: { cert: 'user.cert' },
      },
      features: {
        verifyExports: true,
        disableSourceMaps: false,
      },
      remoteCache: {
        container: 'user-container',
        storageAccount: 'user-account',
      },
    };

    const parentConfig: AppConfig = {
      helpMessage: 'parent help',
      server: {
        port: 8081,
        https: { key: 'key' },
      },
      features: {
        disableSourceMaps: true,
        syncBundles: true,
      },
      remoteCache: {
        container: 'parent-container',
        storageAccount: 'parent-account',
      },
    };

    const rootConfig: AppConfig = {
      server: {
        routes: [{ match: 'rootRoute' }],
      },
      features: {
        verifyExports: false,
      },
    };

    const result = mergeParentConfig({ appConfig, parentConfigs: [rootConfig, parentConfig] });
    expect(result).toEqual({
      helpMessage: 'parent help',
      server: {
        port: 8080,
        domain: 'localhost',
        https: { cert: 'user.cert', key: 'key' },
        routes: [{ match: 'rootRoute' }],
      },
      features: {
        verifyExports: true,
        disableSourceMaps: false,
        syncBundles: true,
      },
      remoteCache: {
        container: 'user-container',
        storageAccount: 'user-account',
      },
    });
  });

  it('overwrites array properties (except packageSettings)', () => {
    const appConfig: AppConfig = {
      extends: 'parent-config',
      dependsOn: [['yarn', ['user-stuff']]],
      server: { port: [8080] },
    };

    const parentConfig: AppConfig = {
      dependsOn: [['yarn', ['parent-stuff']]],
      server: { port: [8081] },
    };

    const result = mergeParentConfig({ appConfig, parentConfigs: [parentConfig] });
    expect(result).toEqual({
      dependsOn: [['yarn', ['user-stuff']]],
      server: { port: [8080] },
    });
  });

  it('concatenates packageSettings', () => {
    const appConfig: AppConfig = {
      extends: ['parent-config', 'extra-config'],
      packageSettings: [
        { match: 'foo', exports: { '.': ['./user-foo'] } },
        { match: 'bar', bundler: 'rollup' },
      ],
    };

    const parentConfig: AppConfig = {
      packageSettings: [{ match: 'foo', bundler: 'ori' }],
    };

    const extraConfig: AppConfig = {
      packageSettings: [{ match: 'extra', bundler: 'ori' }],
    };

    const result = mergeParentConfig({ appConfig, parentConfigs: [parentConfig, extraConfig] });
    expect(result).toEqual({
      packageSettings: [
        // parent config (not merged with user config's foo entry)
        { match: 'foo', bundler: 'ori' },
        // extra config
        { match: 'extra', bundler: 'ori' },
        // user config
        { match: 'foo', exports: { '.': ['./user-foo'] } },
        { match: 'bar', bundler: 'rollup' },
      ],
    });
  });
});
